import { Injectable } from '@angular/core';
import { FilterParams } from '../models/habilitation-history.model';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { JournalisationEvent, JournalisationHistoryResponse, PaginationInfo } from '../models/journalisation.model';

@Injectable({
  providedIn: 'root'
})
export class JournalisationService {

  private baseUrl = `${environment.apiUrl}`;
  private readonly apiUrl = `${this.baseUrl}/users/logings`;

  // BehaviorSubjects pour gérer l'état
  private eventsSubject = new BehaviorSubject<JournalisationEvent[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private paginationSubject = new BehaviorSubject<PaginationInfo | null>(null);
  private errorSubject = new BehaviorSubject<string | null>(null);

  // Observables publics
  public events$ = this.eventsSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public pagination$ = this.paginationSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  private constructor(private http: HttpClient) {}

  /**
   * Récupère la liste des événements d'historique
   */
  getAllLogings(filters: FilterParams = {}): Observable<JournalisationHistoryResponse>   {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    // Construction des paramètres HTTP
    let params = new HttpParams();

    if (filters.operationType) {
      params = params.set('operationType', filters.operationType);
    }

    if (filters.resourceType) {
      params = params.set('resourceType', filters.resourceType);
    }

    if (filters.page !== undefined) {
      params = params.set('page', filters.page.toString());
    }

    if (filters.size !== undefined) {
      params = params.set('size', filters.size.toString());
    }

    if (filters.sort) {
      params = params.set('sort', filters.sort);
    }

    if (filters.startDate) {
      params = params.set('startDate', filters.startDate);
    }

    if (filters.endDate) {
      params = params.set('endDate', filters.endDate);
    }

    if (filters.userId) {
      params = params.set('userId', filters.userId);
    }

    if (filters.clientId) {
      params = params.set('clientId', filters.clientId);
    }

    if (filters.clientUuid) {
      params = params.set('clientUuid', filters.clientUuid);
    }

    return this.http.get<JournalisationHistoryResponse>(this.apiUrl, { params })
      .pipe(
        tap(response => {
          const processedEvents = this.processApiData(response.content);
          this.eventsSubject.next(processedEvents);

          const paginationInfo: PaginationInfo = {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            max: response.max,
            totalElements: response.totalElements,
            first: response.first,
            hasNext: response.hasNext,
            hasPrevious: response.hasPrevious
          };
          this.paginationSubject.next(paginationInfo);

          this.loadingSubject.next(false);
        }),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Traite et enrichit les données d'événements de journalisation
   * @param events - Liste des événements à traiter
   * @returns Liste des événements enrichis
   */
  private processApiData(events: JournalisationEvent[]): JournalisationEvent[] {
    return events.map(event => {
      const dateTime = this.formatDateTime(event.timestamp);

      return {
        ...event,
        ...dateTime,
        operationClass: this.getOperationClass(event.eventType),
        roles: [],
        // Utilisation directe des propriétés de l'événement
        userId: event.user?.id || 'N/A',
        username: event.user?.username || 'N/A',
        userFullName: event.user
          ? `${event.user.firstName || ''} ${event.user.lastName || ''}`.trim() || 'N/A'
          : 'N/A',
        userEmail: event.user?.email || 'N/A',
        clientId: event.clientId || 'N/A',
        ipAddress: event.ipAddress || 'N/A'
      };
    });
  }

  /**
   * Gestion des erreurs HTTP
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    this.loadingSubject.next(false);

    let errorMessage: string;

    if (error.error instanceof ErrorEvent) {
      // Erreur côté client
      errorMessage = `Erreur: ${error.error.message}`;
    } else {
      // Erreur côté serveur
      switch (error.status) {
        case 400:
          errorMessage = 'Requête invalide. Vérifiez les paramètres.';
          break;
        case 401:
          errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
          break;
        case 403:
          errorMessage = 'Accès interdit. Vous n\'avez pas les permissions nécessaires.';
          break;
        case 404:
          errorMessage = 'Ressource non trouvée.';
          break;
        case 500:
          errorMessage = 'Erreur serveur interne.';
          break;
        case 0:
          errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
          break;
        default:
          errorMessage = `Erreur ${error.status}: ${error.message}`;
      }
    }

    this.errorSubject.next(errorMessage);
    console.error('Erreur API:', error);

    return throwError(() => new Error(errorMessage));
  }

  /**
   * Formate la date et l'heure
   */
  private formatDateTime(timestamp: number): { dateString: string; timeString: string } {
    const date = new Date(timestamp);
    return {
      dateString: date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      timeString: date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    };
  }

  /**
   * Obtient la classe CSS pour le type d'opération
   */
  private getOperationClass(operationType: string): string {
    return operationType.toLowerCase();
  }

  /**
   * Réinitialise l'état du service
   */
  resetState(): void {
    this.eventsSubject.next([]);
    this.loadingSubject.next(false);
    this.paginationSubject.next(null);
    this.errorSubject.next(null);
  }
}